/* eslint-disable no-console */
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { getPageSize } from '../constants/pageSize';

async function buildPdfFromElement(element, onProgress = null, pageSize = 'A4') {
  const pages = element.querySelectorAll('.page, .cover-page, .paper-page');

  if (pages.length === 0) {
    throw new Error('페이지를 찾을 수 없습니다.');
  }

  // 페이지 사이즈 설정
  const size = getPageSize(pageSize);
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: size.format,
  });

  const pdfWidth = size.width; // mm

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];

    // 진행 상황 콜백 호출
    if (onProgress) {
      onProgress(i + 1, pages.length);
    }

    // html2canvas 옵션 - 원본 스타일 그대로 캡처 (스타일 변환 없음)
    const canvas = await html2canvas(page, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: false,
      foreignObjectRendering: false,
      removeContainer: true,
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    // 첫 페이지가 아니면 새 페이지 추가
    if (i > 0) {
      pdf.addPage();
    }

    // 이미지를 PDF에 추가
    pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
  }

  return pdf;
}

/**
 * 원본 element를 html2canvas로 캡처하여 PDF 생성
 * @param {HTMLElement} element - PDF로 변환할 element
 * @param {string} filename - 저장할 파일명
 * @param {Function} onProgress - 진행 상황 콜백 (current, total)
 * @param {string} pageSize - 페이지 사이즈 ('A4' 또는 'A5')
 */
async function generatePdfFromElement(element, filename = "document", onProgress = null, pageSize = 'A4') {
  const pdf = await buildPdfFromElement(element, onProgress, pageSize);
  // PDF 저장
  pdf.save(`${filename}.pdf`);
}

/**
 * 원본 element를 html2canvas로 캡처하여 PDF Blob 생성 (파일 업로드용)
 * @param {HTMLElement} element - PDF로 변환할 element
 * @param {Function} onProgress - 진행 상황 콜백 (current, total)
 * @param {string} pageSize - 페이지 사이즈 ('A4' 또는 'A5')
 * @returns {Promise<Blob>}
 */
export async function generatePdfBlobFromElement(element, onProgress = null, pageSize = 'A4') {
  const pdf = await buildPdfFromElement(element, onProgress, pageSize);
  return pdf.output("blob");
}

/**
 * iframe 기반 전체화면 미리보기 (브라우저 인쇄 기능으로 PDF 저장)
 * - 같은 document context를 공유하므로 Vue scoped CSS 완벽 유지
 * - window.print()로 PDF 저장 가능
 * @param {Object} options
 * @param {HTMLElement} options.element - 미리보기할 요소
 * @param {string} options.title - 문서 제목 (선택)
 * @param {string} options.pageSize - 페이지 사이즈 ('A4' 또는 'A5', 기본값: 'A4')
 */
export function openIframePrintPreview({ element, title = "PDF 미리보기", pageSize = 'A4', usePrint = false }) {
  const size = getPageSize(pageSize);
  // 1. Fullscreen overlay 생성
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.9);
    z-index: 99999;
    display: flex;
    flex-direction: column;
  `;

  // 2. 툴바 생성
  const toolbar = document.createElement('div');
  toolbar.style.cssText = `
    background: #333;
    color: white;
    padding: 15px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 10px rgba(0,0,0,0.3);
  `;

  const titleSpan = document.createElement('span');
  titleSpan.textContent = title;
  titleSpan.style.cssText = 'font-size: 16px; font-weight: 600;';

  const buttonContainer = document.createElement('div');
  buttonContainer.style.cssText = 'display: flex; gap: 10px;';

  // PDF 저장 버튼
  const saveBtn = document.createElement('button');
  saveBtn.textContent = 'PDF로 저장';
  saveBtn.style.cssText = `
    background: #1976d2;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: background 0.2s;
  `;
  saveBtn.onmouseover = () => saveBtn.style.background = '#1565c0';
  saveBtn.onmouseout = () => saveBtn.style.background = '#1976d2';
  const waitForIframeReady = async () => {
    await new Promise((resolve) => {
      if (iframe.contentDocument && iframe.contentDocument.readyState === "complete") {
        resolve();
      } else {
        iframe.onload = () => resolve();
      }
    });

    const doc = iframe.contentDocument;
    if (doc?.fonts?.ready) {
      try {
        await doc.fonts.ready;
      } catch (error) {
        console.warn("Font loading wait failed", error);
      }
    }

    const images = Array.from(doc?.images || []);
    if (images.length > 0) {
      await Promise.all(
        images.map((img) => (
          img.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            })
        ))
      );
    }
  };

  saveBtn.onclick = async () => {
    saveBtn.disabled = true;
    const originalText = saveBtn.textContent;

    try {
      if (usePrint) {
        saveBtn.textContent = '인쇄 준비 중...';
        await waitForIframeReady();
        saveBtn.textContent = '인쇄 중...';
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        saveBtn.textContent = originalText;
      } else {
        // 원본 element를 직접 캡처 (Vue scoped CSS 유지)
        await generatePdfFromElement(element, title, (current, total) => {
          saveBtn.textContent = `PDF 생성 중... (${current}/${total})`;
        }, pageSize);
        saveBtn.textContent = 'PDF 저장 완료!';
        setTimeout(() => {
          saveBtn.textContent = originalText;
        }, 2000);
      }
    } catch (error) {
      console.error('PDF 생성 오류:', error);
      alert('PDF 생성에 실패했습니다.');
      saveBtn.textContent = originalText;
    } finally {
      saveBtn.disabled = false;
    }
  };

  // 닫기 버튼
  const closeBtn = document.createElement('button');
  closeBtn.textContent = '닫기';
  closeBtn.style.cssText = `
    background: #424242;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: background 0.2s;
  `;
  closeBtn.onmouseover = () => closeBtn.style.background = '#616161';
  closeBtn.onmouseout = () => closeBtn.style.background = '#424242';
  closeBtn.onclick = () => {
    document.body.removeChild(overlay);
  };

  buttonContainer.appendChild(saveBtn);
  buttonContainer.appendChild(closeBtn);
  toolbar.appendChild(titleSpan);
  toolbar.appendChild(buttonContainer);

  // 3. iframe 컨테이너 생성
  const iframeContainer = document.createElement('div');
  iframeContainer.style.cssText = `
    flex: 1;
    overflow: auto;
    background: #e0e0e0;
    padding: 20px;
  `;

  // 4. iframe 생성
  const iframe = document.createElement('iframe');
  iframe.style.cssText = `
    width: 100%;
    height: 100%;
    border: none;
    background: white;
    display: block;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0,0,0,0.3);
  `;

  iframeContainer.appendChild(iframe);
  overlay.appendChild(toolbar);
  overlay.appendChild(iframeContainer);
  document.body.appendChild(overlay);

  // 5. iframe에 컨텐츠 로드
  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

  // 모든 스타일시트 수집 (link와 style 태그 모두)
  let styleContent = '';

  // 현재 document의 모든 style 태그 복사
  document.querySelectorAll('style').forEach(style => {
    styleContent += style.innerHTML + '\n';
  });

  // 현재 document의 모든 link 태그 (CSS) 복사
  const linkTags = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    .map(link => `<link rel="stylesheet" href="${link.href}">`)
    .join('\n');

  // 인쇄 전용 스타일 추가
  const printStyles = `
    @page {
      size: ${size.name};
      margin: 0;
    }

    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    @media print {
      body {
        margin: 0;
        padding: 0;
      }

      .page, .cover-page {
        box-shadow: none;
        margin: 0;
        page-break-after: always;
        page-break-inside: avoid;
        break-after: page;
        break-inside: avoid;
      }

      .page:last-child, .cover-page:last-child {
        page-break-after: auto;
        break-after: auto;
      }

      /* 테이블 페이지 넘김 방지 */
      table {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }

      /* 테이블 행 페이지 넘김 방지 */
      tr {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }

      /* 큰 테이블의 경우 thead를 각 페이지마다 반복 */
      thead {
        display: table-header-group;
      }

      tfoot {
        display: table-footer-group;
      }
    }

    @media screen {
      body {
        margin: 0;
        padding: 20px;
        background: #e0e0e0;
      }

      .page, .cover-page {
        margin-bottom: 20px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      }

      .page:last-child, .cover-page:last-child {
        margin-bottom: 0;
      }
    }
  `;

  // HTML 구조 작성
  iframeDoc.open();
  iframeDoc.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <base href="${window.location.origin}/">
      <title>${title}</title>
      ${linkTags}
      <style>${styleContent}</style>
      <style>${printStyles}</style>
    </head>
    <body>
      ${element.innerHTML}
    </body>
    </html>
  `);
  iframeDoc.close();

  // ESC 키로 닫기
  const handleEscape = (e) => {
    if (e.key === 'Escape') {
      document.body.removeChild(overlay);
      document.removeEventListener('keydown', handleEscape);
    }
  };
  document.addEventListener('keydown', handleEscape);
}

/**
 * 브라우저 Print API를 사용한 빠른 PDF 미리보기/생성
 * @param {Object} options
 * @param {HTMLElement} options.element - 인쇄할 요소
 * @param {string} options.title - 문서 제목 (선택)
 * @param {string} options.pageSize - 페이지 사이즈 ('A4' 또는 'A5', 기본값: 'A4')
 */
export function printPdf({ element, title = "미리보기", pageSize = 'A4' }) {
  const size = getPageSize(pageSize);

  // 인쇄 전용 iframe 생성
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;

  // 기본 스타일 + 인쇄 스타일
  const printStyles = `
    @page {
      size: ${size.name};
      margin: 0;
    }

    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    body {
      margin: 0;
      padding: 0;
      background: white;
    }

    .page, .cover-page {
      width: ${size.width}mm;
      min-height: ${size.height}mm;
      padding: 0;
      margin: 0;
      background: white;
      page-break-after: always;
      page-break-inside: avoid;
      box-sizing: border-box;
      overflow: hidden;
    }

    .page:last-child, .cover-page:last-child {
      page-break-after: auto;
    }

    /* 테이블 스타일 */
    table {
      border-collapse: collapse;
      width: auto;
      max-width: 100%;
    }

    table, th, td {
      border: 1px solid #ddd;
    }

    th, td {
      padding: 8px 12px;
      text-align: left;
    }

    thead th {
      background-color: #f5f5f5 !important;
      font-weight: 600;
    }

    /* 폰트 */
    @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;600;700&display=swap');

    body {
      font-family: 'Noto Sans KR', sans-serif;
    }
  `;

  // HTML 구성
  doc.open();
  doc.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${title}</title>
      <style>${printStyles}</style>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;600;700&display=swap" rel="stylesheet">
    </head>
    <body>
      ${element.innerHTML}
    </body>
    </html>
  `);
  doc.close();

  // 폰트 로딩 대기 후 인쇄
  iframe.contentWindow.onload = () => {
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();

      // 인쇄 후 iframe 제거
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 500);
  };
}

/**
 * 새 창에서 PDF 미리보기 (브라우저 인쇄 기능 사용)
 * @param {Object} options
 * @param {HTMLElement} options.element - 인쇄할 요소
 * @param {string} options.title - 문서 제목 (선택)
 * @param {string} options.pageSize - 페이지 사이즈 ('A4' 또는 'A5', 기본값: 'A4')
 */
export function openPrintPreview({ element, title = "PDF 미리보기", pageSize = 'A4' }) {
  const size = getPageSize(pageSize);

  // 새 창 열기
  const printWindow = window.open("", "_blank", "width=900,height=700");

  if (!printWindow) {
    alert("팝업이 차단되었습니다. 팝업 차단을 해제해 주세요.");
    return;
  }

  // 원본 요소를 deep clone하고 계산된 스타일을 인라인으로 적용
  const clonedElement = element.cloneNode(true);
  applyInlineStyles(element, clonedElement);
  const originalHTML = clonedElement.innerHTML;

  // 추가 인쇄 스타일
  const printStyles = `
    @page {
      size: ${size.name};
      margin: 0;
    }

    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }

    body {
      margin: 0;
      padding: 20px;
      background: #e0e0e0;
      font-family: 'Noto Sans KR', sans-serif;
    }

    .print-toolbar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #333;
      color: white;
      padding: 10px 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      z-index: 1000;
    }

    .print-toolbar button {
      background: #1976d2;
      color: white;
      border: none;
      padding: 8px 20px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      margin-left: 10px;
    }

    .print-toolbar button:hover {
      background: #1565c0;
    }

    .content-wrapper {
      padding-top: 60px;
      max-width: ${size.width}mm;
      margin: 0 auto;
    }

    /* 테이블 스타일 - 원본 너비 유지 */
    table, .content-wrapper table, div table, body table {
      width: auto !important;
      max-width: 100% !important;
      border-collapse: collapse !important;
      border-spacing: 0 !important;
      border: 1px solid #dddddd !important;
      table-layout: auto !important;
      font-size: 13px !important;
      letter-spacing: -0.03em !important;
      margin: 10px 0 !important;
    }

    table caption, div table caption, body table caption {
      padding: 12px !important;
      caption-side: top !important;
      color: #333333 !important;
      text-align: center !important;
      font-size: 13px !important;
      font-weight: 600 !important;
      letter-spacing: 0.5px !important;
      border: none !important;
      background: transparent !important;
    }

    table thead th, table thead td,
    div table thead th, body table thead th {
      padding: 12px 16px !important;
      border: 1px solid #dddddd !important;
      text-align: left !important;
      vertical-align: middle !important;
      background-color: #f5f5f5 !important;
      color: #333333 !important;
      font-weight: 600 !important;
    }

    table tbody th, table tfoot th,
    div table tbody th, body table tbody th {
      padding: 12px 16px !important;
      border: 1px solid #dddddd !important;
      text-align: left !important;
      vertical-align: middle !important;
      font-weight: normal !important;
      background-color: transparent !important;
    }

    table td, table tbody td, table tfoot td,
    div table td, body table td {
      padding: 12px 16px !important;
      border: 1px solid #dddddd !important;
      text-align: left !important;
      vertical-align: middle !important;
      word-break: break-word !important;
      white-space: normal !important;
      background-color: transparent !important;
    }

    table th, div table th, body table th {
      padding: 12px 16px !important;
      border: 1px solid #dddddd !important;
      text-align: left !important;
      vertical-align: middle !important;
    }

    /* 첫 번째 행 (헤더로 사용되는 경우) */
    table tr:first-child th,
    table tr:first-child td {
      background-color: #f5f5f5 !important;
      font-weight: 600 !important;
    }

    /* 본문 텍스트 스타일 */
    .page-content p, div p {
      font-size: 12.39px !important;
      line-height: 22.24px !important;
      letter-spacing: -0.47px !important;
      word-break: break-all !important;
      font-weight: 300 !important;
      color: #000000 !important;
      margin-bottom: 5px !important;
    }

    strong, b {
      font-weight: 900 !important;
    }

    h1, h2, h3, h4, h5, h6 {
      color: #000000 !important;
      border: none !important;
      border-bottom: none !important;
    }

    ol li, ul li {
      font-size: 15px !important;
      line-height: 21px !important;
      letter-spacing: -0.03em !important;
      margin-left: 5px !important;
      padding-left: 3px !important;
      word-break: break-all !important;
    }

    @media print {
      .print-toolbar {
        display: none !important;
      }

      body {
        background: white;
        padding: 0;
      }

      .content-wrapper {
        padding-top: 0;
      }

      .page, .cover-page {
        box-shadow: none;
        margin: 0;
      }
    }
  `;

  // 기본 HTML 구조 작성
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <base href="${window.location.origin}/">
      <title>${title}</title>
      <!-- 폰트 로드 -->
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;600;700&display=swap" rel="stylesheet">
      <link href="https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        /* 인쇄 색상 보존 */
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }

        /* 추가 인쇄 전용 스타일 */
        ${printStyles}
      </style>
    </head>
    <body>
      <div class="print-toolbar">
        <span>${title}</span>
        <div>
          <button onclick="window.print()">PDF로 저장 / 인쇄</button>
          <button onclick="window.close()">닫기</button>
        </div>
      </div>
      <div class="content-wrapper">
        ${originalHTML}
      </div>
    </body>
    </html>
  `);
  printWindow.document.close();

  // 문서 로드 후 테이블에 직접 인라인 스타일 적용
  printWindow.onload = () => {
    applyTableStyles(printWindow.document);
  };

  // 즉시 적용도 시도 (onload가 안 될 경우 대비)
  setTimeout(() => {
    applyTableStyles(printWindow.document);
  }, 100);
}

/**
 * 원본 요소의 계산된 스타일을 복제된 요소에 인라인으로 적용 (재귀적)
 * @param {HTMLElement} original - 원본 요소
 * @param {HTMLElement} clone - 복제된 요소
 */
function applyInlineStyles(original, clone) {
  // 레이아웃과 시각적 스타일에 필요한 속성들
  const styleProps = [
    // 레이아웃
    'display', 'position', 'top', 'right', 'bottom', 'left',
    'flex', 'flex-direction', 'flex-wrap', 'justify-content', 'align-items', 'align-content', 'gap',
    'grid-template-columns', 'grid-template-rows', 'grid-gap',
    // 크기
    'width', 'height', 'min-width', 'min-height', 'max-width', 'max-height',
    'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
    'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
    'box-sizing',
    // 테두리
    'border', 'border-width', 'border-style', 'border-color',
    'border-top', 'border-right', 'border-bottom', 'border-left',
    'border-radius', 'border-collapse', 'border-spacing',
    // 배경
    'background', 'background-color', 'background-image', 'background-size', 'background-position',
    // 텍스트
    'color', 'font-family', 'font-size', 'font-weight', 'font-style',
    'line-height', 'letter-spacing', 'text-align', 'text-decoration', 'text-transform',
    'white-space', 'word-break', 'word-wrap', 'overflow-wrap',
    // 기타
    'overflow', 'overflow-x', 'overflow-y', 'vertical-align',
    'opacity', 'visibility', 'z-index', 'transform',
    'table-layout', 'caption-side'
  ];

  // 원본 요소의 스타일 적용
  if (original.nodeType === 1 && clone.nodeType === 1) {
    const computedStyle = window.getComputedStyle(original);
    let inlineStyle = '';

    // 항상 적용해야 하는 속성들 (none/auto/normal 값도 유의미)
    const alwaysApply = [
      'display', 'position', 'flex-direction', 'justify-content', 'align-items',
      'font-family', 'font-size', 'font-weight', 'font-style', 'line-height',
      'color', 'background-color', 'text-align', 'white-space'
    ];

    styleProps.forEach(prop => {
      const value = computedStyle.getPropertyValue(prop);
      if (!value || value === '') return;

      // 중요 속성은 항상 적용, 나머지는 기본값 제외
      if (alwaysApply.includes(prop)) {
        inlineStyle += `${prop}: ${value}; `;
      } else if (value !== 'none' && value !== 'auto' && value !== 'normal') {
        inlineStyle += `${prop}: ${value}; `;
      }
    });

    if (inlineStyle) {
      const existingStyle = clone.getAttribute('style') || '';
      clone.setAttribute('style', existingStyle + inlineStyle);
    }
  }

  // 자식 요소들에 대해 재귀적으로 적용
  const originalChildren = original.children;
  const cloneChildren = clone.children;

  for (let i = 0; i < originalChildren.length && i < cloneChildren.length; i++) {
    applyInlineStyles(originalChildren[i], cloneChildren[i]);
  }
}

/**
 * 테이블 요소에 직접 인라인 스타일 적용
 * @param {Document} doc - 대상 문서
 */
function applyTableStyles(doc) {
  const tables = doc.querySelectorAll("table");

  tables.forEach((table) => {
    // 테이블 기본 스타일 - width: auto로 원본 스타일 유지
    table.style.cssText = `
      width: auto !important;
      max-width: 100% !important;
      border-collapse: collapse !important;
      border-spacing: 0 !important;
      border: 1px solid #dddddd !important;
      table-layout: auto !important;
      font-size: 13px !important;
      letter-spacing: -0.03em !important;
      margin: 10px 0 !important;
    `;

    // 테이블에 thead가 있는지 확인
    const thead = table.querySelector('thead');
    const hasTableRowHeaderColored = table.classList.contains('table-row-header-colored');

    // 모든 td 기본 스타일
    const tds = table.querySelectorAll("td");
    tds.forEach((td) => {
      td.style.cssText = `
        padding: 12px 16px !important;
        border: 1px solid #dddddd !important;
        text-align: left !important;
        vertical-align: middle !important;
        word-break: break-word !important;
        white-space: normal !important;
      `;
    });

    // 1. thead 안의 모든 셀(th, td 둘 다)에 헤더 색상 적용
    if (thead) {
      const theadCells = thead.querySelectorAll('th, td');
      theadCells.forEach(cell => {
        cell.style.setProperty('background-color', '#f5f5f5', 'important');
        cell.style.setProperty('color', '#333', 'important');
        cell.style.setProperty('font-weight', '600', 'important');
        cell.style.setProperty('padding', '12px 16px', 'important');
        cell.style.setProperty('border', '1px solid #ddd', 'important');
      });
    } else {
      // 2. thead가 없으면 첫 번째 행을 헤더로 처리
      const tbody = table.querySelector('tbody');
      const firstRow = tbody ? tbody.querySelector('tr:first-child') : table.querySelector('tr:first-child');

      if (firstRow) {
        const firstRowCells = firstRow.querySelectorAll('th, td');
        firstRowCells.forEach(cell => {
          cell.style.setProperty('background-color', '#f5f5f5', 'important');
          cell.style.setProperty('color', '#333', 'important');
          cell.style.setProperty('font-weight', '600', 'important');
          cell.style.setProperty('padding', '12px 16px', 'important');
          cell.style.setProperty('border', '1px solid #ddd', 'important');
        });
      }
    }

    // 3. table-row-header-colored: tbody/tfoot의 th 요소에도 배경색 적용
    if (hasTableRowHeaderColored) {
      // thead의 첫 번째 열
      if (thead) {
        const theadFirstColCells = thead.querySelectorAll('th:first-child, td:first-child');
        theadFirstColCells.forEach(cell => {
          cell.style.setProperty('background-color', '#f5f5f5', 'important');
          cell.style.setProperty('color', '#333', 'important');
        });
      }

      // tbody의 th 요소들
      const tbodyCells = table.querySelectorAll('tbody th');
      tbodyCells.forEach(th => {
        th.style.setProperty('background-color', '#f5f5f5', 'important');
        th.style.setProperty('color', '#333', 'important');
        th.style.setProperty('padding', '12px 16px', 'important');
        th.style.setProperty('border', '1px solid #ddd', 'important');
      });

      // tfoot의 th 요소들
      const tfootCells = table.querySelectorAll('tfoot th');
      tfootCells.forEach(th => {
        th.style.setProperty('background-color', '#f5f5f5', 'important');
        th.style.setProperty('color', '#333', 'important');
        th.style.setProperty('padding', '12px 16px', 'important');
        th.style.setProperty('border', '1px solid #ddd', 'important');
      });
    }
  });
}

/**
 * 페이지별로 개별 캡처하여 PDF 생성 (canvas 크기 제한 우회)
 * @deprecated 브라우저 Print API 사용 권장 (printPdf, openPrintPreview)
 * @param {Object} options
 * @param {string} options.pageSize - 페이지 사이즈 ('A4' 또는 'A5', 기본값: 'A4')
 */
export async function generateFinalPdf({
  element,
  imageQuality = 0.92,
  pageSize = 'A4',
}) {
  const size = getPageSize(pageSize);
  element.style.display = "block";
  element.style.backgroundColor = "white";

  try {
    // 모든 페이지 요소 찾기 (.page, .cover-page 클래스)
    const pages = element.querySelectorAll(".page, .cover-page");

    if (pages.length === 0) {
      console.error("페이지 요소를 찾을 수 없습니다.");
      element.style.display = "none";
      return;
    }

    // PDF 생성
    const pdf = new jsPDF({
      unit: "mm",
      format: size.format,
      orientation: "portrait",
    });

    const pdfWidth = size.width;
    const pdfHeight = size.height;

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];

      // 캡처 전에 원본 DOM에 직접 스타일 적용 (백업용 배열)
      const styleBackups = [];
      const tables = page.querySelectorAll('table');

      tables.forEach(table => {
        const backup = { table, cells: [] };

        // 테이블에 thead가 있는지 확인
        const thead = table.querySelector('thead');
        const hasTableRowHeaderColored = table.classList.contains('table-row-header-colored');

        // 1. thead 안의 모든 셀(th, td 둘 다)에 헤더 색상 적용
        if (thead) {
          const theadCells = thead.querySelectorAll('th, td');
          if (theadCells.length > 0) {
            theadCells.forEach(cell => {
              backup.cells.push({
                element: cell,
                originalStyle: cell.getAttribute('style') || ''
              });
              cell.style.setProperty('background-color', '#f5f5f5', 'important');
              cell.style.setProperty('color', '#333', 'important');
              cell.style.setProperty('font-weight', '600', 'important');
              cell.style.setProperty('padding', '12px 16px', 'important');
              cell.style.setProperty('border', '1px solid #ddd', 'important');
            });
          }
        } else {
          // 2. thead가 없으면 첫 번째 행을 헤더로 처리
          const tbody = table.querySelector('tbody');
          const firstRow = tbody ? tbody.querySelector('tr:first-child') : table.querySelector('tr:first-child');

          if (firstRow) {
            const firstRowCells = firstRow.querySelectorAll('th, td');
            firstRowCells.forEach(cell => {
              backup.cells.push({
                element: cell,
                originalStyle: cell.getAttribute('style') || ''
              });
              cell.style.setProperty('background-color', '#f5f5f5', 'important');
              cell.style.setProperty('color', '#333', 'important');
              cell.style.setProperty('font-weight', '600', 'important');
              cell.style.setProperty('padding', '12px 16px', 'important');
              cell.style.setProperty('border', '1px solid #ddd', 'important');
            });
          }
        }

        // 3. table-row-header-colored: tbody/tfoot의 th 요소에도 배경색 적용
        if (hasTableRowHeaderColored) {
          // thead의 첫 번째 열
          if (thead) {
            const theadFirstColCells = thead.querySelectorAll('th:first-child, td:first-child');
            theadFirstColCells.forEach(cell => {
              if (!backup.cells.find(b => b.element === cell)) {
                backup.cells.push({
                  element: cell,
                  originalStyle: cell.getAttribute('style') || ''
                });
              }
              cell.style.setProperty('background-color', '#f5f5f5', 'important');
              cell.style.setProperty('color', '#333', 'important');
            });
          }

          // tbody의 th 요소들
          const tbodyCells = table.querySelectorAll('tbody th');
          tbodyCells.forEach(th => {
            if (!backup.cells.find(b => b.element === th)) {
              backup.cells.push({
                element: th,
                originalStyle: th.getAttribute('style') || ''
              });
            }
            th.style.setProperty('background-color', '#f5f5f5', 'important');
            th.style.setProperty('color', '#333', 'important');
            th.style.setProperty('padding', '12px 16px', 'important');
            th.style.setProperty('border', '1px solid #ddd', 'important');
          });

          // tfoot의 th 요소들
          const tfootCells = table.querySelectorAll('tfoot th');
          tfootCells.forEach(th => {
            if (!backup.cells.find(b => b.element === th)) {
              backup.cells.push({
                element: th,
                originalStyle: th.getAttribute('style') || ''
              });
            }
            th.style.setProperty('background-color', '#f5f5f5', 'important');
            th.style.setProperty('color', '#333', 'important');
            th.style.setProperty('padding', '12px 16px', 'important');
            th.style.setProperty('border', '1px solid #ddd', 'important');
          });
        }

        styleBackups.push(backup);
      });

      // 각 페이지를 canvas로 캡처
      const canvas = await html2canvas(page, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#FFFFFF",
        logging: false,
        windowWidth: 794,
        scrollY: 0,
        scrollX: 0,
      });

      // 원본 스타일 복원
      styleBackups.forEach(backup => {
        backup.cells.forEach(({ element, originalStyle }) => {
          if (originalStyle) {
            element.setAttribute('style', originalStyle);
          } else {
            element.removeAttribute('style');
          }
        });
      });

      // canvas를 이미지로 변환
      const imgData = canvas.toDataURL("image/jpeg", imageQuality);

      // 첫 페이지가 아니면 새 페이지 추가
      if (i > 0) {
        pdf.addPage();
      }

      // 이미지를 PDF에 추가 (페이지 전체 크기로)
      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
    }

    // 원래 상태로 되돌림
    element.style.display = "none";

    // PDF를 Blob으로 변환
    const pdfBlob = pdf.output("blob");

    // Blob URL 생성
    const blobUrl = URL.createObjectURL(pdfBlob);

    // 새 창에서 PDF 열기
    window.open(blobUrl, "_blank");

  } catch (error) {
    element.style.display = "none";
    console.error("PDF 생성 오류:", error);
  }
}

/**
 * 미리보기용 PDF 생성 (페이지별 개별 캡처)
 * @param {Object} options
 * @param {string} options.pageSize - 페이지 사이즈 ('A4' 또는 'A5', 기본값: 'A4')
 */
export async function generateFinalPdfWithPreview({
  element,
  imageQuality = 0.92,
  pageSize = 'A4',
}) {
  const size = getPageSize(pageSize);

  try {
    // 모든 페이지 요소 찾기
    const pages = element.querySelectorAll(".page, .cover-page");

    if (pages.length === 0) {
      console.error("페이지 요소를 찾을 수 없습니다.");
      return;
    }

    // PDF 생성
    const pdf = new jsPDF({
      unit: "mm",
      format: size.format,
      orientation: "portrait",
    });

    const pdfWidth = size.width;
    const pdfHeight = size.height;

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];

      // 캡처 전에 원본 DOM에 직접 스타일 적용 (백업용 배열)
      const styleBackups = [];
      const tables = page.querySelectorAll('table');

      tables.forEach(table => {
        const backup = { table, cells: [] };

        // 테이블에 thead가 있는지 확인
        const thead = table.querySelector('thead');
        const hasTableRowHeaderColored = table.classList.contains('table-row-header-colored');

        // 1. thead 안의 모든 셀(th, td 둘 다)에 헤더 색상 적용
        if (thead) {
          const theadCells = thead.querySelectorAll('th, td');
          if (theadCells.length > 0) {
            theadCells.forEach(cell => {
              backup.cells.push({
                element: cell,
                originalStyle: cell.getAttribute('style') || ''
              });
              cell.style.setProperty('background-color', '#f5f5f5', 'important');
              cell.style.setProperty('color', '#333', 'important');
              cell.style.setProperty('font-weight', '600', 'important');
              cell.style.setProperty('padding', '12px 16px', 'important');
              cell.style.setProperty('border', '1px solid #ddd', 'important');
            });
          }
        } else {
          // 2. thead가 없으면 첫 번째 행을 헤더로 처리
          const tbody = table.querySelector('tbody');
          const firstRow = tbody ? tbody.querySelector('tr:first-child') : table.querySelector('tr:first-child');

          if (firstRow) {
            const firstRowCells = firstRow.querySelectorAll('th, td');
            firstRowCells.forEach(cell => {
              backup.cells.push({
                element: cell,
                originalStyle: cell.getAttribute('style') || ''
              });
              cell.style.setProperty('background-color', '#f5f5f5', 'important');
              cell.style.setProperty('color', '#333', 'important');
              cell.style.setProperty('font-weight', '600', 'important');
              cell.style.setProperty('padding', '12px 16px', 'important');
              cell.style.setProperty('border', '1px solid #ddd', 'important');
            });
          }
        }

        // 3. table-row-header-colored: tbody/tfoot의 th 요소에도 배경색 적용
        if (hasTableRowHeaderColored) {
          // thead의 첫 번째 열
          if (thead) {
            const theadFirstColCells = thead.querySelectorAll('th:first-child, td:first-child');
            theadFirstColCells.forEach(cell => {
              if (!backup.cells.find(b => b.element === cell)) {
                backup.cells.push({
                  element: cell,
                  originalStyle: cell.getAttribute('style') || ''
                });
              }
              cell.style.setProperty('background-color', '#f5f5f5', 'important');
              cell.style.setProperty('color', '#333', 'important');
            });
          }

          // tbody의 th 요소들
          const tbodyCells = table.querySelectorAll('tbody th');
          tbodyCells.forEach(th => {
            if (!backup.cells.find(b => b.element === th)) {
              backup.cells.push({
                element: th,
                originalStyle: th.getAttribute('style') || ''
              });
            }
            th.style.setProperty('background-color', '#f5f5f5', 'important');
            th.style.setProperty('color', '#333', 'important');
            th.style.setProperty('padding', '12px 16px', 'important');
            th.style.setProperty('border', '1px solid #ddd', 'important');
          });

          // tfoot의 th 요소들
          const tfootCells = table.querySelectorAll('tfoot th');
          tfootCells.forEach(th => {
            if (!backup.cells.find(b => b.element === th)) {
              backup.cells.push({
                element: th,
                originalStyle: th.getAttribute('style') || ''
              });
            }
            th.style.setProperty('background-color', '#f5f5f5', 'important');
            th.style.setProperty('color', '#333', 'important');
            th.style.setProperty('padding', '12px 16px', 'important');
            th.style.setProperty('border', '1px solid #ddd', 'important');
          });
        }

        styleBackups.push(backup);
      });

      // 각 페이지를 canvas로 캡처
      const canvas = await html2canvas(page, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#FFFFFF",
        logging: false,
        windowWidth: 794,
        scrollY: 0,
        scrollX: 0,
      });

      // 원본 스타일 복원
      styleBackups.forEach(backup => {
        backup.cells.forEach(({ element, originalStyle }) => {
          if (originalStyle) {
            element.setAttribute('style', originalStyle);
          } else {
            element.removeAttribute('style');
          }
        });
      });

      // canvas를 이미지로 변환
      const imgData = canvas.toDataURL("image/jpeg", imageQuality);

      // 첫 페이지가 아니면 새 페이지 추가
      if (i > 0) {
        pdf.addPage();
      }

      // 이미지를 PDF에 추가
      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
    }

    // PDF를 Blob으로 변환
    const pdfBlob = pdf.output("blob");

    // Blob URL 생성
    const blobUrl = URL.createObjectURL(pdfBlob);

    // 새 창에서 PDF 열기
    window.open(blobUrl, "_blank");

  } catch (error) {
    console.error("PDF 생성 오류:", error);
  }
}
